import { useState, useCallback, useEffect } from 'react';
import auditService, {
  AuditLog,
  AuditLogsFilter,
  AuditStats,
  AuditAlert,
  CreateAuditAlertInput,
  AuditExport,
  LoginAttempt,
  DataAccessLog,
} from '../services/audit.service';

// ==================== useAuditLogs ====================

export interface UseAuditLogsReturn {
  logs: AuditLog[];
  total: number;
  page: number;
  totalPages: number;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  filter: AuditLogsFilter;
  setFilter: (filter: AuditLogsFilter) => void;
  loadLogs: (filter?: AuditLogsFilter) => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useAuditLogs(initialFilter: AuditLogsFilter = {}): UseAuditLogsReturn {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<AuditLogsFilter>(initialFilter);

  const loadLogs = useCallback(async (newFilter?: AuditLogsFilter) => {
    const currentFilter = newFilter || filter;
    setLoading(true);
    setError(null);
    try {
      const response = await auditService.getAuditLogs(currentFilter);
      setLogs(response.data);
      setTotal(response.total);
      setPage(response.page);
      setTotalPages(response.totalPages);
      setHasMore(response.hasMore);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load audit logs';
      setError(message);
      console.error('Error loading audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;

    setLoading(true);
    try {
      const response = await auditService.getAuditLogs({
        ...filter,
        page: page + 1,
      });
      setLogs((prev) => [...prev, ...response.data]);
      setPage(response.page);
      setHasMore(response.hasMore);
    } catch (err) {
      console.error('Error loading more audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, page, hasMore, loading]);

  const refresh = useCallback(async () => {
    await loadLogs({ ...filter, page: 1 });
  }, [filter, loadLogs]);

  useEffect(() => {
    loadLogs();
  }, []);

  return {
    logs,
    total,
    page,
    totalPages,
    hasMore,
    loading,
    error,
    filter,
    setFilter: (newFilter: AuditLogsFilter) => {
      setFilter(newFilter);
      loadLogs(newFilter);
    },
    loadLogs,
    loadMore,
    refresh,
  };
}

// ==================== useAuditStats ====================

export interface UseAuditStatsReturn {
  stats: AuditStats | null;
  loading: boolean;
  error: string | null;
  loadStats: () => Promise<void>;
}

export function useAuditStats(): UseAuditStatsReturn {
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await auditService.getAuditStats();
      setStats(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load audit stats';
      setError(message);
      console.error('Error loading audit stats:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  return { stats, loading, error, loadStats };
}

// ==================== useResourceHistory ====================

export interface UseResourceHistoryReturn {
  history: AuditLog[];
  loading: boolean;
  error: string | null;
  loadHistory: (resource: string, resourceId: string, limit?: number) => Promise<void>;
}

export function useResourceHistory(): UseResourceHistoryReturn {
  const [history, setHistory] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(
    async (resource: string, resourceId: string, limit?: number) => {
      setLoading(true);
      setError(null);
      try {
        const data = await auditService.getResourceHistory(resource, resourceId, limit);
        setHistory(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load resource history';
        setError(message);
        console.error('Error loading resource history:', err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { history, loading, error, loadHistory };
}

// ==================== useAuditAlerts ====================

export interface UseAuditAlertsReturn {
  alerts: AuditAlert[];
  loading: boolean;
  error: string | null;
  loadAlerts: () => Promise<void>;
  createAlert: (input: CreateAuditAlertInput) => Promise<AuditAlert>;
  updateAlert: (alertId: string, input: Partial<CreateAuditAlertInput>) => Promise<AuditAlert>;
  deleteAlert: (alertId: string) => Promise<void>;
  toggleAlert: (alertId: string, isActive: boolean) => Promise<AuditAlert>;
}

export function useAuditAlerts(): UseAuditAlertsReturn {
  const [alerts, setAlerts] = useState<AuditAlert[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await auditService.getAlerts();
      setAlerts(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load alerts';
      setError(message);
      console.error('Error loading alerts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createAlert = useCallback(async (input: CreateAuditAlertInput) => {
    const alert = await auditService.createAlert(input);
    setAlerts((prev) => [alert, ...prev]);
    return alert;
  }, []);

  const updateAlert = useCallback(
    async (alertId: string, input: Partial<CreateAuditAlertInput>) => {
      const updated = await auditService.updateAlert(alertId, input);
      setAlerts((prev) =>
        prev.map((a) => (a.id === alertId ? updated : a))
      );
      return updated;
    },
    []
  );

  const deleteAlert = useCallback(async (alertId: string) => {
    await auditService.deleteAlert(alertId);
    setAlerts((prev) => prev.filter((a) => a.id !== alertId));
  }, []);

  const toggleAlert = useCallback(
    async (alertId: string, isActive: boolean) => {
      const updated = await auditService.toggleAlert(alertId, isActive);
      setAlerts((prev) =>
        prev.map((a) => (a.id === alertId ? updated : a))
      );
      return updated;
    },
    []
  );

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  return {
    alerts,
    loading,
    error,
    loadAlerts,
    createAlert,
    updateAlert,
    deleteAlert,
    toggleAlert,
  };
}

// ==================== useAuditExport ====================

export interface UseAuditExportReturn {
  exportStatus: AuditExport | null;
  exporting: boolean;
  error: string | null;
  startExport: (options: {
    format: 'csv' | 'json' | 'xlsx';
    startDate?: string;
    endDate?: string;
  }) => Promise<void>;
  checkStatus: (exportId: string) => Promise<void>;
}

export function useAuditExport(): UseAuditExportReturn {
  const [exportStatus, setExportStatus] = useState<AuditExport | null>(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startExport = useCallback(
    async (options: {
      format: 'csv' | 'json' | 'xlsx';
      startDate?: string;
      endDate?: string;
    }) => {
      setExporting(true);
      setError(null);
      try {
        const { exportId } = await auditService.createExport(options);
        // Poll for status
        let status = await auditService.getExportStatus(exportId);
        setExportStatus(status);

        while (status.status === 'pending' || status.status === 'processing') {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          status = await auditService.getExportStatus(exportId);
          setExportStatus(status);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Export failed';
        setError(message);
        console.error('Export error:', err);
      } finally {
        setExporting(false);
      }
    },
    []
  );

  const checkStatus = useCallback(async (exportId: string) => {
    try {
      const status = await auditService.getExportStatus(exportId);
      setExportStatus(status);
    } catch (err) {
      console.error('Error checking export status:', err);
    }
  }, []);

  return { exportStatus, exporting, error, startExport, checkStatus };
}

// ==================== useLoginAttempts ====================

export interface UseLoginAttemptsReturn {
  attempts: LoginAttempt[];
  loading: boolean;
  error: string | null;
  loadAttempts: (options?: {
    email?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
  }) => Promise<void>;
}

export function useLoginAttempts(): UseLoginAttemptsReturn {
  const [attempts, setAttempts] = useState<LoginAttempt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAttempts = useCallback(
    async (options?: {
      email?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const data = await auditService.getLoginAttempts(options);
        setAttempts(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load login attempts';
        setError(message);
        console.error('Error loading login attempts:', err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { attempts, loading, error, loadAttempts };
}

// ==================== useDataAccessLogs ====================

export interface UseDataAccessLogsReturn {
  logs: DataAccessLog[];
  loading: boolean;
  error: string | null;
  loadLogs: (options?: {
    dataType?: string;
    userId?: string;
    resourceId?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
  }) => Promise<void>;
}

export function useDataAccessLogs(): UseDataAccessLogsReturn {
  const [logs, setLogs] = useState<DataAccessLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLogs = useCallback(
    async (options?: {
      dataType?: string;
      userId?: string;
      resourceId?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const data = await auditService.getDataAccessLogs(options);
        setLogs(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load data access logs';
        setError(message);
        console.error('Error loading data access logs:', err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { logs, loading, error, loadLogs };
}

export default useAuditLogs;
